import { matchesValueTrigger as matchesValueTriggerShared, parseValueTrigger, triggerToBoundedInterval as triggerToBoundedIntervalShared } from './value-trigger.js';
import { isNoSensorData, NO_SENSOR_DATA_TRIGGER } from './sensor-health.js';
import { coverageGaps, isIntervalFullyCovered, mergeCoverageIntervals, rangesOverlap } from './interval-coverage.js';
/**
 * Angle's two adapters onto the shared trigger vocabulary.
 *
 * The domain bounds are the point: a rotation has no -Infinity, so `lt:30` covers 0 to 30 rather
 * than everything below 30, and that bound is what makes angle's coverage arithmetic terminate.
 *
 * The epsilon stays angle's own `1e-9`. It is safe here — a 0-360 domain never holds values where
 * `1e-9` is a meaningful fraction — and changing it would change grading with no bug behind it.
 * Slider needed the tighter one precisely because its domain is author-defined and can be tiny.
 */
function matchesValueTrigger(trigger, value) {
    return matchesValueTriggerShared(trigger, value, EPS);
}
function triggerToBoundedInterval(trigger) {
    return triggerToBoundedIntervalShared(trigger, ROTATION_DOMAIN_LO, ROTATION_DOMAIN_HI);
}
/** This block's own float tolerance, preserved — see `mergeIntervals`'s note on the two epsilons. */
const isIntervalFullyCoveredEps = (t, c) => isIntervalFullyCovered(t, c, EPS);
import { overlappingAnswerPairs } from './answer-overlap';
import { answerHalfWidths } from './answer-tolerance.js';
import { optionalOutcome, SET_ANSWER_TRIGGER, participationOutcome } from './grading-mode.js';
import { resolveScenarioValueSetup } from './scenario.js';
const EPS = (n) => 1e-9 * Math.max(1, Math.abs(n));
/**
 * Angle is a closed loop, not an open line — 0° and 360° are the same point, and no value
 * outside [0, 360] can ever be authored or submitted. Every interval-producing function below
 * bounds its one-sided ends to these edges (never ±Infinity, unlike numerical.ts's/
 * slider.ts's own unbounded precedent), and computeRotationAcceptedRanges additionally *wraps*
 * an exact answer's tolerance band across the 0/360 seam when it overshoots either edge.
 */
const ROTATION_DOMAIN_LO = 0;
const ROTATION_DOMAIN_HI = 360;
/**
 * Splits a tolerance band that may overshoot [0, 360] into one or two real intervals that
 * stay within the domain, treating 0 and 360 as the same point. E.g. 360° ± 2° (naive [358,
 * 362]) becomes [{358, 360}, {0, 2}]; 0° ± 3° (naive [-3, 3]) becomes the same split via the
 * negative side. A band wide enough to cover the whole loop collapses to the full domain.
 */
function wrapRotationInterval(lo, hi) {
    const span = hi - lo;
    const closed = { loInclusive: true, hiInclusive: true };
    if (span >= 360)
        return [{ lo: ROTATION_DOMAIN_LO, hi: ROTATION_DOMAIN_HI, ...closed }];
    let nlo = lo % 360;
    if (nlo < 0)
        nlo += 360;
    const nhi = nlo + span;
    if (nhi <= 360 + EPS(360))
        return [{ lo: nlo, hi: Math.min(nhi, ROTATION_DOMAIN_HI), ...closed }];
    return [{ lo: nlo, hi: ROTATION_DOMAIN_HI, ...closed }, { lo: ROTATION_DOMAIN_LO, hi: nhi - 360, ...closed }];
}
/**
 * The accepted range(s) implied by the canonical condition, expressed as closed intervals
 * bounded to the [0, 360] domain (one-sided conditions bound to the domain's own edge rather
 * than ±Infinity — see the domain note above). Multiple exact/between answers are OR'd
 * together — matching any one of them is correct. Ported from numerical.ts's/slider.ts's
 * computeAcceptedRanges, with two angle-specific differences: the domain bound, and exact
 * answers wrap across the 0/360 seam via wrapRotationInterval instead of ever landing outside it.
 */
/** This block's own reading of a trigger as coverage — `exact` adds none (a point never closes a
 *  gap). Injected into the shared merge so the interval maths is shared and the trigger vocabulary
 *  stays here. */
function rotationTriggerSpan(trigger) {
    if (parseValueTrigger(trigger)?.type === 'exact')
        return null;
    return triggerToBoundedInterval(trigger);
}
export function computeRotationAcceptedRanges(config) {
    const { conditionType, answers, betweenAnswers, threshold } = config;
    if (conditionType === 'exact') {
        return answers.filter(a => Number.isFinite(a.value)).flatMap(a => {
            const { below: loErr, above: hiErr } = answerHalfWidths(a, config);
            return wrapRotationInterval(a.value - loErr, a.value + hiErr);
        });
    }
    if (conditionType === 'between') {
        return betweenAnswers.filter(a => Number.isFinite(a.low) && Number.isFinite(a.high))
            .map(a => ({ lo: a.low, hi: a.high, loInclusive: true, hiInclusive: true }));
    }
    if (conditionType === 'lt' || conditionType === 'lte') {
        return [{ lo: ROTATION_DOMAIN_LO, hi: threshold, loInclusive: true, hiInclusive: conditionType === 'lte' }];
    }
    return [{ lo: threshold, hi: ROTATION_DOMAIN_HI, loInclusive: conditionType === 'gte', hiInclusive: true }];
}
/** Is this value within the accepted condition? Handles lt/gt's exclusive boundary explicitly. */
export function isRotationWithinAcceptedRange(value, config) {
    if (config.conditionType === 'lt')
        return value < config.threshold;
    if (config.conditionType === 'lte')
        return value <= config.threshold;
    if (config.conditionType === 'gt')
        return value > config.threshold;
    if (config.conditionType === 'gte')
        return value >= config.threshold;
    return computeRotationAcceptedRanges(config).some(r => value >= r.lo - EPS(r.lo) && value <= r.hi + EPS(r.hi));
}
/**
 * Returns the feedback states that match a student's angle measurement.
 *
 * Evaluation sequence — one fewer gate than evaluateGPS: there's no device-reported
 * confidence value for tilt/orientation the way GPS's Geolocation API accuracy radius
 * provides, so there is no accuracy check and no poor_accuracy state at all. Otherwise the
 * same shape as evaluateSliderHandle (minus the touched-position/step concerns, which don't
 * apply to a sensor reading):
 * 1. optional mode → any_response, measured or not — no correctness check at all for this
 *    mode (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Untouched (graded/survey only) → unanswered.
 * 3. survey mode → any_answer (any measurement counts as participation).
 * 4. graded mode: rotationDegrees checked directly against the accepted condition (exact/
 *    between/lt/lte/gt/gte) → `correct`; else all matching targeted value-feedback
 *    triggers fire in parallel (same lt/lte/gt/gte/range/exact vocabulary, applied to the
 *    raw value), else `incorrect`.
 */
/**
 * How far off the calibration plane a gesture may drift before the reading stops being about the
 * axis the teacher asked for.
 *
 * Exported and shared with the block's LIVE warning, which had its own private copy of 8. Two
 * numbers for one threshold is how a student gets warned at 8 and graded at something else.
 */
export const ROTATION_OFF_AXIS_CUTOFF_DEG = 8;
/**
 * The targeted states a READING fires outside graded: the value triggers it matches, plus Set
 * Answer when it lands inside the accepted band.
 *
 * Shared by survey and optional so the two cannot drift. Set Answer resolves against the band
 * derivation the correct card already uses, which is what keeps it in step with an edited answer
 * and makes it work with a formula answer.
 */
function participationMatches(reading, states, config) {
    return states.filter(st => st.type !== 'targeted' ? false
        : st.trigger === SET_ANSWER_TRIGGER ? isRotationWithinAcceptedRange(reading, config)
            : matchesValueTrigger(st.trigger, reading));
}
/**
 * What varies per VARIANT on a Rotation block — the answer setup and nothing else.
 *
 * Rotation has no question bank of its own (nothing calls `addFieldVersion` for it), so this
 * exists for the SCENARIO projection rather than for versions. Named and shaped like its
 * siblings' anyway: the two uses share one projection, and a block that later gains a bank
 * should not need a second list that can disagree with this one.
 */
export const ROTATION_VERSION_KEYS = ['conditionType', 'answers', 'betweenAnswers', 'toleranceSymmetry', 'threshold'];
/**
 * This block's answer setup for the scenario in force — the value family's shared rule, with
 * only this block's own field list and fail-closed shape supplied.
 *
 * Empty answer lists are what "no row" means: nothing can be correct, which is the same
 * fail-closed choice the choice family makes by going all-neutral.
 */
export function resolveRotationScenario(d, scenarioId) {
    return resolveScenarioValueSetup(d, scenarioId, ROTATION_VERSION_KEYS, { answers: [], betweenAnswers: [] });
}
export function evaluateRotation(answer, 
/** What the tracker managed. Angle is the one sensor whose evaluator was structurally blind to
 *  reading quality — an off-axis gesture graded exactly as a clean one — and this is where that
 *  gets fixed. */
health, rawBlockData, 
/** The leader's variant in force — see `resolveScenarioValueSetup`. */
scenarioId) {
    const blockData = resolveRotationScenario(rawBlockData, scenarioId);
    const { touched, rotationDegrees } = answer;
    const { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold, gradingMode, feedback } = blockData;
    // `acceptedConfig` is hoisted here rather than declared with the graded matcher below,
    // because the survey branch reads it too and the later `rangeConfig` sits beneath that
    // branch. `readingIsMeasurable` went with optional: the gates own a missing reading now.
    const acceptedConfig = { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold };
    /**
     * OPTIONAL fires targeted states too, which it did not.
     *
     * This returned `any_response` before the reading was looked at, so a value trigger authored
     * in optional could never fire and the editor listed chips that were dead. Survey has always
     * reached the matcher; optional was the odd one out.
     *
     * The GATES stay above it: no reading means nothing to match, which is a blank.
     */
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(feedback.states);
    }
    // The device failed with no way round it. ABOVE the blank check, because `touched` is false in
    // both cases and this is the more specific one: a student who could not measure is not a student
    // who did not try. Below `optional`, which short-circuits everything by design — a block nobody
    // is grading has no problem to route.
    if (isNoSensorData(health)) {
        const noData = feedback.states.find(s => s.type === 'default' && s.trigger === NO_SENSOR_DATA_TRIGGER);
        return noData ? [noData] : [];
    }
    if (!touched) {
        const unanswered = feedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    /*
     * The phone left the plane it was calibrated in, so the number is about a different axis than
     * the one the teacher asked for.
     *
     * SENSOR answers only, and the same reason compass skips its gates for a typed one: the gate
     * judges an instrument, and there is no instrument behind a number somebody typed.
     *
     * This is the half Angle was missing. The block has warned about an off-axis gesture live for as
     * long as it has tracked one — but the warning ended at submission, `evaluateRotation` never saw
     * `offAxisDegrees`, and a student who ignored it was graded as though the reading were clean.
     * Marked wrong, that is, for a measurement the sensor mis-took.
     */
    if (answer.method === 'sensor'
        && health.offAxisDegrees != null
        && health.offAxisDegrees > ROTATION_OFF_AXIS_CUTOFF_DEG) {
        const poorAxis = feedback.states.find(s => s.type === 'default' && s.trigger === 'poor_axis');
        return poorAxis ? [poorAxis] : [];
    }
    /** The targeted states this reading fires. ONE matcher, used by every grading mode — see
     *  `participationOutcome`. A reading is measurable whether or not any reading is the RIGHT
     *  one, so it still means something in a survey. */
    const targetedForReading = () => participationMatches(rotationDegrees, feedback.states, acceptedConfig);
    // Targeted value states fire here too. Whether one CAN fire is analysed against the accepted
    // band, and outside graded there is no band in force — correct is never checked, so nothing is
    // subsumed by it. The editor's own analysis is mode-aware to match.
    if (gradingMode === 'survey') {
        return participationOutcome(feedback.states, 'survey', targetedForReading());
    }
    const rangeConfig = { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold };
    if (isRotationWithinAcceptedRange(rotationDegrees, rangeConfig)) {
        const correct = feedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForReading();
    if (matched.length > 0)
        return matched;
    const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
// ─── Authoring-time reachability analysis ──────────────────────────────────────
// Ported from numerical.ts's/slider.ts's reachability/collision analysis, unchanged in
// behavior — the accepted condition is now potentially multiple OR'd ranges (not a single
// interval-or-null the way GPS's target+tolerance was), so `acceptedRanges` is an array.
/**
 * Whether Default Incorrect is genuinely unreachable — every point on the [0, 360] loop is
 * covered by either the accepted condition(s) or some value feedback. Defined directly in
 * terms of computeRotationDefaultIncorrectGaps (rather than checking for a single merged
 * interval spanning the whole domain) because a wrapped exact answer's coverage can
 * legitimately land as *two* merged pieces (one touching 0, one touching 360) that are
 * contiguous around the seam — "no gaps" is the correct test; "exactly one merged interval"
 * is not, once wraparound is in play.
 */
export function isRotationDefaultIncorrectUnreachable(acceptedRanges, feedbackTriggers) {
    return computeRotationDefaultIncorrectGaps(acceptedRanges, feedbackTriggers).length === 0;
}
/**
 * The gaps that actually reach Default Incorrect — the [0, 360] loop's complement of the
 * accepted range(s) plus any value feedback coverage. Bounded to the domain's own edges (never
 * ±Infinity — see the domain note above computeRotationAcceptedRanges): coverage that reaches
 * both 0 and 360 leaves no outer gap on either end, which is what correctly represents a
 * wrapped exact answer (e.g. 0° ± 3° covers [357, 360] and [0, 3] as two merged pieces with no
 * gap between them at the seam, while the real gap in the middle of the loop is reported
 * normally). Formatting/wording is the caller's job. Empty array means Default Incorrect is
 * unreachable (matches isRotationDefaultIncorrectUnreachable — this is effectively its inverse).
 */
export function computeRotationDefaultIncorrectGaps(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, rotationTriggerSpan, EPS);
    return coverageGaps(merged, ROTATION_DOMAIN_LO, ROTATION_DOMAIN_HI, EPS);
}
/**
 * Whether a targeted feedback trigger's *entire* condition now falls inside the accepted
 * range(s) — i.e. it can never actually fire, since Correct is checked first and is terminal
 * (see evaluateRotation's sequence).
 *
 * This used to close BOTH sides, defending it as a safe over-approximation. That argument holds for
 * the TARGET — a closed target is a superset of the open one, so containment stays conservative —
 * but not for the ACCEPTED range, where closing it over-approximates COVERAGE and reports
 * containment that isn't there. With Correct = "< 5", a targeted feedback at exactly 5 (a reachable
 * wrong answer) was called fully subsumed. Both sides now carry their own boundaries.
 */
export function isRotationFeedbackFullySubsumed(trigger, acceptedRanges) {
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return isIntervalFullyCoveredEps(target, acceptedRanges);
}
/**
 * Whether a targeted feedback trigger's target interval intersects the accepted
 * Correct range(s) at all — a strictly weaker condition than isRotationFeedbackFullySubsumed
 * (which requires the *entire* target to be covered). A shared boundary is "real" overlap only when
 * BOTH sides admit that point — Correct = "<= 10" and a trigger of "> 10" are perfect complements
 * with no shared point at all.
 */
export function doesRotationFeedbackOverlapAccepted(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed || parsed.type === 'exact')
        return false;
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return acceptedRanges.some(r => {
        if (target.hi < r.lo || r.hi < target.lo)
            return false;
        if (target.hi === r.lo)
            return target.hiInclusive && r.loInclusive;
        if (r.hi === target.lo)
            return r.hiInclusive && target.loInclusive;
        return true;
    });
}
/**
 * Pairs of ACCEPTED ANSWERS that cover some of the same angles.
 *
 * Seam-aware for free, and that is the point. On a closed circle 0 and 360 are the same attitude, so
 * "0 +/- 2" and "360 +/- 2" are the identical answer written two ways — but nothing said so, because
 * a numeric duplicate check compares 0 against 360 and finds them 360 apart. Running the comparison
 * on the intervals AFTER wrapRotationInterval has split them fixes it without a special case: both
 * spellings resolve to [358,360] and [0,2], which plainly intersect.
 *
 * Returns answer ids rather than intervals so a caller can point at the offending rows. A warning,
 * never an error: overlapping answers still grade correctly, since acceptance is their union. What is
 * wrong is the authoring, where two rows that look different mean one thing.
 */
export function findRotationOverlappingAnswerPairs(config) {
    const { conditionType, answers, betweenAnswers } = config;
    // Only the multi-answer conditions can overlap with themselves. A single threshold is one interval
    // by construction, so there is no second thing for it to collide with.
    const perAnswer = conditionType === 'exact'
        ? answers.map(a => {
            const { below: loErr, above: hiErr } = answerHalfWidths(a, config);
            return { id: a.id, intervals: wrapRotationInterval(a.value - loErr, a.value + hiErr) };
        })
        : conditionType === 'between'
            ? betweenAnswers.map(a => ({ id: a.id, intervals: [{ lo: Math.min(a.low, a.high), hi: Math.max(a.low, a.high) }] }))
            : [];
    // Comparison is shared — see `overlappingAnswerPairs`. Only the interval BUILDING above is
    // this block's own, because only this block knows how its domain behaves.
    return overlappingAnswerPairs(perAnswer);
}
/**
 * Pairs of currently-authored targeted feedback triggers whose ranges overlap each
 * other — likely unintentional (a teacher probably meant non-overlapping bands like
 * 10°-20°, 20°-30°, but wrote two open-ended "greater than" conditions that both cover
 * everything past the smaller threshold instead). 'exact' triggers are excluded, mirroring
 * numerical.ts's/slider.ts's findOverlappingFeedbackPairs — a specific callout value nested
 * inside a broader band is a common, deliberate authoring pattern, not a mistake.
 */
export function findRotationOverlappingFeedbackPairs(feedbackTriggers) {
    const parsed = feedbackTriggers
        .map(trigger => {
        const p = parseValueTrigger(trigger);
        if (!p || p.type === 'exact')
            return null;
        if (p.type === 'lt')
            return { trigger, lo: ROTATION_DOMAIN_LO, hi: p.n1, loInclusive: true, hiInclusive: false };
        if (p.type === 'lte')
            return { trigger, lo: ROTATION_DOMAIN_LO, hi: p.n1, loInclusive: true, hiInclusive: true };
        if (p.type === 'gt')
            return { trigger, lo: p.n1, hi: ROTATION_DOMAIN_HI, loInclusive: false, hiInclusive: true };
        if (p.type === 'gte')
            return { trigger, lo: p.n1, hi: ROTATION_DOMAIN_HI, loInclusive: true, hiInclusive: true };
        return { trigger, lo: Math.min(p.n1, p.n2), hi: Math.max(p.n1, p.n2), loInclusive: true, hiInclusive: true };
    })
        .filter((x) => x !== null);
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (rangesOverlap(a, b))
                pairs.push([a.trigger, b.trigger]);
        }
    }
    return pairs;
}
//# sourceMappingURL=rotation.js.map